// src/pages/BuildingSentry.jsx
// Building Sentry - AI-Powered Smart Building Operations
// Edge AI Platform for Commercial Real Estate & Facility Management
// MWC Barcelona Demo - Celerway Stratus

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ════════════════════════════════════════════════════════════════════════════
// MOCK BUILDING DATA - Simulates smart building operations
// ════════════════════════════════════════════════════════════════════════════

const MOCK_BUILDING_GATEWAY = {
  id: 'gateway-tower-one',
  name: 'Tower One BMS Gateway',
  location: 'Meridian Plaza, 45 Floors',
  status: 'Online',
  assetCount: 18,
  activeAlerts: 6,
  stratusModel: 'Stratus 5G Building',
  connectivity: '5G + Building Ethernet',
  lastSync: new Date().toISOString(),
  occupancy: 72,           // % current occupancy
  maxOccupancy: 2400,      // people
  currentOccupants: 1728,
  energyToday: 4.2,        // MWh
  energyTarget: 4.8,       // MWh budget
  greenScore: 87,          // LEED/sustainability score
};

const MOCK_ASSETS = [
  // HVAC - Chillers
  {
    id: 'CH-01',
    type: 'Chiller',
    category: 'hvac',
    model: 'Carrier 23XRV 500-ton',
    location: 'Central Plant - Basement',
    status: 'running',
    metrics: {
      loadPercent: 68,
      supplyTemp: 44,          // °F chilled water supply
      returnTemp: 56,
      deltaT: 12,
      kWPerTon: 0.58,          // Efficiency
      baselineKW: 0.52,
      runHours: 18450,
      refrigerantCharge: 98,   // % of spec
      condensorApproach: 2.1,  // °F
    },
  },
  {
    id: 'CH-02',
    type: 'Chiller',
    category: 'hvac',
    model: 'Carrier 23XRV 500-ton',
    location: 'Central Plant - Basement',
    status: 'standby',
    metrics: {
      loadPercent: 0,
      supplyTemp: null,
      returnTemp: null,
      deltaT: null,
      kWPerTon: null,
      baselineKW: 0.52,
      runHours: 12200,
      refrigerantCharge: 96,
      condensorApproach: null,
    },
  },
  // HVAC - Air Handling Units
  {
    id: 'AHU-01',
    type: 'Air Handler',
    category: 'hvac',
    model: 'Trane M-Series 40,000 CFM',
    location: 'Mechanical Room - Floor 15',
    status: 'running',
    metrics: {
      supplyAirTemp: 55,       // °F
      setpoint: 55,
      returnAirTemp: 74,
      mixedAirTemp: 62,
      supplyFanSpeed: 78,      // %
      returnFanSpeed: 72,
      filterDP: 1.2,           // inches WC (water column)
      filterAlarm: 1.5,        // Alarm threshold
      outsideAirDamper: 25,    // %
      coolingValve: 65,        // %
      heatingValve: 0,
      staticPressure: 1.8,     // inches WC
    },
    zonesServed: 'Floors 12-18',
  },
  {
    id: 'AHU-03',
    type: 'Air Handler',
    category: 'hvac',
    model: 'Trane M-Series 35,000 CFM',
    location: 'Mechanical Room - Floor 30',
    status: 'warning',
    metrics: {
      supplyAirTemp: 67,       // WAY above setpoint!
      setpoint: 55,
      returnAirTemp: 76,
      mixedAirTemp: 71,
      supplyFanSpeed: 92,      // Running hard
      returnFanSpeed: 88,
      filterDP: 1.4,
      filterAlarm: 1.5,
      outsideAirDamper: 15,
      coolingValve: 100,       // Maxed out
      heatingValve: 0,
      staticPressure: 1.6,
      damperFault: true,       // Suspected stuck damper
    },
    zonesServed: 'Floors 28-35',
  },
  // Elevators
  {
    id: 'ELEV-01',
    type: 'Elevator',
    category: 'elevator',
    model: 'Otis Gen2',
    location: 'Main Bank - Car 1',
    status: 'operational',
    metrics: {
      currentFloor: 23,
      direction: 'up',
      doorsStatus: 'closed',
      doorDwellTime: 3.2,      // seconds (normal)
      baselineDwell: 3.0,
      tripsToday: 287,
      avgWaitTime: 18,         // seconds
      motorTemp: 142,          // °F
      brakeWear: 78,           // % remaining
      lastInspection: '2024-12-15',
    },
  },
  {
    id: 'ELEV-02',
    type: 'Elevator',
    category: 'elevator',
    model: 'Otis Gen2',
    location: 'Main Bank - Car 2',
    status: 'warning',
    metrics: {
      currentFloor: 8,
      direction: 'stopped',
      doorsStatus: 'open',
      doorDwellTime: 4.8,      // SLOW - sensor issue?
      baselineDwell: 3.0,
      tripsToday: 245,
      avgWaitTime: 24,         // Above target
      motorTemp: 156,          // Warm
      brakeWear: 45,           // Getting low
      lastInspection: '2024-12-15',
      faultCode: 'W-12: Door timing extended',
    },
  },
  {
    id: 'ELEV-03',
    type: 'Elevator',
    category: 'elevator',
    model: 'Otis Gen2',
    location: 'Main Bank - Car 3',
    status: 'operational',
    metrics: {
      currentFloor: 41,
      direction: 'down',
      doorsStatus: 'closed',
      doorDwellTime: 3.1,
      baselineDwell: 3.0,
      tripsToday: 312,
      avgWaitTime: 16,
      motorTemp: 138,
      brakeWear: 82,
      lastInspection: '2024-12-15',
    },
  },
  // Access Control
  {
    id: 'ACC-LOBBY',
    type: 'Access Point',
    category: 'access',
    model: 'HID Turnstile Bank',
    location: 'Main Lobby',
    status: 'operational',
    metrics: {
      passagesToday: 2847,
      deniedToday: 12,
      avgThroughput: 42,       // per minute at peak
      tailgatingEvents: 2,
      lastDenied: '2025-01-31T14:23:00Z',
      lastDeniedBadge: '4421',
      lastDeniedReason: 'Floor restriction',
    },
  },
  {
    id: 'ACC-PARKING',
    type: 'Access Point',
    category: 'access',
    model: 'HID Proximity Reader',
    location: 'Parking Garage Entry',
    status: 'operational',
    metrics: {
      passagesToday: 892,
      deniedToday: 3,
      gateOperations: 895,
      avgDwellTime: 2.4,       // seconds per vehicle
    },
  },
  {
    id: 'ACC-FLOOR35',
    type: 'Access Point',
    category: 'access',
    model: 'HID iCLASS SE',
    location: 'Executive Floor 35',
    status: 'alert',
    metrics: {
      passagesToday: 124,
      deniedToday: 8,          // Elevated!
      lastDenied: '2025-01-31T14:58:00Z',
      lastDeniedBadge: '4421',
      lastDeniedReason: 'Unauthorized - 3rd attempt',
      securityAlert: true,
    },
  },
  // Fire/Life Safety
  {
    id: 'FIRE-PANEL',
    type: 'Fire Alarm Panel',
    category: 'fire',
    model: 'Notifier NFS2-3030',
    location: 'Fire Command - Lobby',
    status: 'operational',
    metrics: {
      systemStatus: 'Normal',
      zonesOnline: 847,
      zonesTotal: 850,
      zonesInAlarm: 0,
      zonesTrouble: 3,
      lastTest: '2025-01-28',
      nextTest: '2025-02-28',
      batteryStatus: 'OK',
      groundFault: false,
    },
  },
  {
    id: 'SMOKE-L15-04',
    type: 'Smoke Detector',
    category: 'fire',
    model: 'System Sensor 2W-B',
    location: 'Floor 15 - Open Office',
    status: 'warning',
    metrics: {
      sensitivity: 1.2,        // %/ft obscuration
      baselineSensitivity: 1.5,
      driftPercent: 20,        // Drifted 20% below spec
      chamberValue: 0.8,
      lastCleaning: '2024-06-15',
      testResult: 'Marginal',
    },
  },
  // Lighting
  {
    id: 'LIGHT-ZONE-B3',
    type: 'Lighting Zone',
    category: 'lighting',
    model: 'Lutron Quantum',
    location: 'Floor 22 - Zone B3',
    status: 'warning',
    metrics: {
      currentLevel: 85,        // % brightness
      setpoint: 75,
      occupancyState: 'Occupied',
      hvacMode: 'Unoccupied',  // MISMATCH!
      daylightLevel: 42,       // fc from sensors
      energyToday: 12.4,       // kWh
      fixtures: 48,
      fixturesOnline: 46,
    },
  },
  {
    id: 'LIGHT-ZONE-A1',
    type: 'Lighting Zone',
    category: 'lighting',
    model: 'Lutron Quantum',
    location: 'Floor 10 - Zone A1',
    status: 'operational',
    metrics: {
      currentLevel: 0,
      setpoint: 0,
      occupancyState: 'Unoccupied',
      hvacMode: 'Unoccupied',
      daylightLevel: 0,
      energyToday: 2.1,
      fixtures: 36,
      fixturesOnline: 36,
    },
  },
  // Energy/Power
  {
    id: 'METER-MAIN',
    type: 'Main Meter',
    category: 'energy',
    model: 'Schneider ION8650',
    location: 'Main Electrical Room',
    status: 'operational',
    metrics: {
      demandKW: 1842,
      peakToday: 2156,
      peakLimit: 2500,         // Demand limit
      energyToday: 4.2,        // MWh
      powerFactor: 0.94,
      voltage: 480,
      thd: 4.2,                // % Total Harmonic Distortion
    },
  },
  {
    id: 'SOLAR-ROOF',
    type: 'Solar Array',
    category: 'energy',
    model: 'SunPower Maxeon 400W',
    location: 'Rooftop',
    status: 'operational',
    metrics: {
      currentOutput: 145,      // kW
      capacityKW: 280,
      energyToday: 0.82,       // MWh
      efficiency: 94,          // % of expected
      invertersOnline: 8,
      invertersTotal: 8,
      panelTemp: 52,           // °C
    },
  },
  // Water
  {
    id: 'WATER-MAIN',
    type: 'Water Meter',
    category: 'water',
    model: 'Badger M-Series',
    location: 'Main Water Entry',
    status: 'operational',
    metrics: {
      flowRate: 45,            // GPM current
      usageToday: 12450,       // gallons
      avgDaily: 11200,
      peakFlow: 120,
      pressure: 65,            // PSI
    },
  },
  {
    id: 'LEAK-L3-REST',
    type: 'Leak Sensor',
    category: 'water',
    model: 'Moen Flo',
    location: 'Floor 3 - Restroom',
    status: 'alarm',
    metrics: {
      flowDetected: true,
      flowRate: 2.1,           // GPM - continuous!
      duration: 47,            // minutes of continuous flow
      expectedFlow: 0,         // Should be 0 at night
      leakConfidence: 94,
    },
  },
  // Parking
  {
    id: 'PARK-L1',
    type: 'Parking Level',
    category: 'parking',
    model: 'ParkAssist M4',
    location: 'Parking Level 1',
    status: 'operational',
    metrics: {
      totalSpaces: 180,
      occupied: 156,
      available: 24,
      evChargers: 12,
      evInUse: 9,
      evAvailable: 3,
      handicap: 8,
      handicapAvailable: 3,
    },
  },
  {
    id: 'EV-CHARGER-07',
    type: 'EV Charger',
    category: 'parking',
    model: 'ChargePoint CT4000',
    location: 'Parking L1 - Space 47',
    status: 'fault',
    metrics: {
      state: 'FAULT',
      faultCode: 'GF-01: Ground fault detected',
      lastSession: '2025-01-31T08:45:00Z',
      energyDelivered: 12.4,   // kWh before fault
      vehicleConnected: true,
      vehicleOwner: 'Badge 3892',
    },
  },
];

// ════════════════════════════════════════════════════════════════════════════
// AI STATUS TYPES - Building specific
// ════════════════════════════════════════════════════════════════════════════

const AI_STATUS = {
  CRITICAL: { icon: '🔴', text: 'CRITICAL', bg: 'bg-red-500', glow: 'from-red-500/50 to-rose-500/50', color: 'text-red-400' },
  WARNING: { icon: '🟡', text: 'WARNING', bg: 'bg-amber-500', glow: 'from-amber-500/50 to-yellow-500/50', color: 'text-amber-400' },
  OPTIMIZING: { icon: '⚡', text: 'OPTIMIZING', bg: 'bg-purple-500', glow: 'from-purple-500/50 to-violet-500/50', color: 'text-purple-400', pulse: true },
  NORMAL: { icon: '✓', text: 'NORMAL', bg: 'bg-emerald-500', glow: 'from-emerald-500/30 to-teal-500/30', color: 'text-emerald-400' },
  STANDBY: { icon: '○', text: 'STANDBY', bg: 'bg-slate-500', glow: 'from-slate-500/30 to-gray-500/30', color: 'text-slate-400' },
};

const CATEGORY_STYLES = {
  hvac: { icon: '🌡️', color: 'text-blue-300', bg: 'from-blue-400/30 to-cyan-500/30', border: 'border-blue-400/60', label: 'HVAC' },
  elevator: { icon: '🛗', color: 'text-purple-300', bg: 'from-purple-400/30 to-violet-500/30', border: 'border-purple-400/60', label: 'Elevator' },
  access: { icon: '🔐', color: 'text-amber-300', bg: 'from-amber-400/30 to-yellow-500/30', border: 'border-amber-400/60', label: 'Access' },
  fire: { icon: '🔥', color: 'text-red-300', bg: 'from-red-400/30 to-orange-500/30', border: 'border-red-400/60', label: 'Fire Safety' },
  lighting: { icon: '💡', color: 'text-yellow-300', bg: 'from-yellow-400/30 to-amber-500/30', border: 'border-yellow-400/60', label: 'Lighting' },
  energy: { icon: '⚡', color: 'text-emerald-300', bg: 'from-emerald-400/30 to-teal-500/30', border: 'border-emerald-400/60', label: 'Energy' },
  water: { icon: '💧', color: 'text-cyan-300', bg: 'from-cyan-400/30 to-blue-500/30', border: 'border-cyan-400/60', label: 'Water' },
  parking: { icon: '🅿️', color: 'text-slate-300', bg: 'from-slate-400/30 to-gray-500/30', border: 'border-slate-400/60', label: 'Parking' },
};

// ════════════════════════════════════════════════════════════════════════════
// AI ANOMALY DETECTION - Building Operations
// ════════════════════════════════════════════════════════════════════════════

const detectBuildingAnomalies = (assets, gateway) => {
  const anomalies = [];
  
  assets.forEach(asset => {
    const m = asset.metrics;
    
    // HVAC - AHU temperature deviation
    if (asset.type === 'Air Handler') {
      if (m.supplyAirTemp && m.setpoint && Math.abs(m.supplyAirTemp - m.setpoint) > 5) {
        const deviation = m.supplyAirTemp - m.setpoint;
        anomalies.push({
          id: `ahu-temp-${asset.id}`,
          type: 'SUPPLY_TEMP_DEVIATION',
          assetId: asset.id,
          severity: Math.abs(deviation) > 10 ? 'critical' : 'warning',
          metric: `${m.supplyAirTemp}°F (SP: ${m.setpoint}°F)`,
          description: `Supply air ${deviation > 0 ? 'above' : 'below'} setpoint by ${Math.abs(deviation)}°F. ${m.damperFault ? 'Damper actuator fault suspected.' : 'Check cooling valve and coil.'}`,
          recommendation: m.damperFault 
            ? 'Dispatch technician to inspect damper actuator. May be stuck or failed. Check BACnet overrides.'
            : 'Verify cooling valve operation. Check chilled water supply temperature and flow.',
          confidence: m.damperFault ? 94 : 86,
          action: {
            type: 'DISPATCH_TECH',
            description: 'Create work order',
          },
        });
      }
      
      // Filter DP approaching alarm
      if (m.filterDP && m.filterAlarm && m.filterDP > m.filterAlarm * 0.9) {
        anomalies.push({
          id: `filter-${asset.id}`,
          type: 'FILTER_CHANGE_DUE',
          assetId: asset.id,
          severity: m.filterDP >= m.filterAlarm ? 'critical' : 'warning',
          metric: `${m.filterDP}" WC`,
          description: 'Filter differential pressure approaching alarm. Restricted airflow affecting efficiency.',
          recommendation: 'Schedule filter replacement. Current DP indicates significant loading.',
          confidence: 97,
          action: {
            type: 'SCHEDULE_FILTER',
            description: 'Order and schedule replacement',
          },
        });
      }
    }
    
    // HVAC - Chiller efficiency
    if (asset.type === 'Chiller' && asset.status === 'running') {
      if (m.kWPerTon && m.baselineKW && m.kWPerTon > m.baselineKW * 1.15) {
        const percentIncrease = Math.floor((m.kWPerTon / m.baselineKW - 1) * 100);
        anomalies.push({
          id: `chiller-eff-${asset.id}`,
          type: 'CHILLER_EFFICIENCY_DROP',
          assetId: asset.id,
          severity: percentIncrease > 20 ? 'warning' : 'info',
          metric: `${m.kWPerTon} kW/ton (+${percentIncrease}%)`,
          description: `Chiller efficiency ${percentIncrease}% below baseline. Increased energy consumption.`,
          recommendation: 'Check condenser approach temp and tube fouling. May need condenser cleaning.',
          confidence: 88,
        });
      }
    }
    
    // Elevator - Door timing
    if (asset.category === 'elevator') {
      if (m.doorDwellTime && m.baselineDwell && m.doorDwellTime > m.baselineDwell * 1.3) {
        const percentIncrease = Math.floor((m.doorDwellTime / m.baselineDwell - 1) * 100);
        anomalies.push({
          id: `elev-door-${asset.id}`,
          type: 'ELEVATOR_DOOR_SLOW',
          assetId: asset.id,
          severity: percentIncrease > 50 ? 'critical' : 'warning',
          metric: `${m.doorDwellTime}s (+${percentIncrease}%)`,
          description: `Door dwell time ${percentIncrease}% above normal. Safety edge sensor may be degrading.`,
          recommendation: 'Schedule elevator technician. Check door operator, safety edge, and interlock sensors.',
          confidence: 91,
          action: {
            type: 'SCHEDULE_ELEVATOR',
            description: 'Request Otis service call',
          },
        });
      }
      
      // Brake wear
      if (m.brakeWear && m.brakeWear < 50) {
        anomalies.push({
          id: `elev-brake-${asset.id}`,
          type: 'BRAKE_WEAR_LOW',
          assetId: asset.id,
          severity: m.brakeWear < 30 ? 'critical' : 'warning',
          metric: `${m.brakeWear}% remaining`,
          description: 'Elevator brake wear approaching replacement threshold.',
          recommendation: 'Schedule brake inspection and replacement during next planned outage.',
          confidence: 95,
        });
      }
    }
    
    // Access Control - Security alert
    if (asset.category === 'access' && m.securityAlert) {
      anomalies.push({
        id: `access-${asset.id}`,
        type: 'SECURITY_ALERT',
        assetId: asset.id,
        severity: 'critical',
        metric: `Badge ${m.lastDeniedBadge}`,
        description: `Multiple unauthorized access attempts at ${asset.location}. Badge ${m.lastDeniedBadge} denied ${m.deniedToday} times today.`,
        recommendation: 'Alert security immediately. Review badge holder profile. Consider temporary badge suspension.',
        confidence: 96,
        action: {
          type: 'ALERT_SECURITY',
          description: 'Notify security team',
        },
      });
    }
    
    // Fire Safety - Detector drift
    if (asset.type === 'Smoke Detector') {
      if (m.driftPercent && m.driftPercent > 15) {
        anomalies.push({
          id: `smoke-${asset.id}`,
          type: 'DETECTOR_DRIFT',
          assetId: asset.id,
          severity: m.driftPercent > 25 ? 'critical' : 'warning',
          metric: `${m.driftPercent}% drift`,
          description: 'Smoke detector sensitivity drifting below specification. May fail to detect smoke promptly.',
          recommendation: 'Schedule detector cleaning or replacement. Test after service.',
          confidence: 89,
          action: {
            type: 'SCHEDULE_FIRE_SERVICE',
            description: 'Create fire system work order',
          },
        });
      }
    }
    
    // Lighting - HVAC sync mismatch
    if (asset.category === 'lighting') {
      if (m.occupancyState === 'Occupied' && m.hvacMode === 'Unoccupied') {
        anomalies.push({
          id: `light-sync-${asset.id}`,
          type: 'OCCUPANCY_MISMATCH',
          assetId: asset.id,
          severity: 'warning',
          metric: 'Sync Error',
          description: `Zone shows occupied (lighting on) but HVAC is in unoccupied mode. Tenant comfort affected.`,
          recommendation: 'Verify BMS schedule sync. Check occupancy sensor integration between lighting and HVAC systems.',
          confidence: 93,
          action: {
            type: 'SYNC_SYSTEMS',
            description: 'Force HVAC to occupied mode',
          },
        });
      }
    }
    
    // Energy - Demand approaching limit
    if (asset.type === 'Main Meter') {
      const demandPercent = (m.demandKW / m.peakLimit) * 100;
      if (demandPercent > 80) {
        anomalies.push({
          id: `demand-${asset.id}`,
          type: 'DEMAND_WARNING',
          assetId: asset.id,
          severity: demandPercent > 90 ? 'critical' : 'warning',
          metric: `${m.demandKW} kW (${Math.floor(demandPercent)}%)`,
          description: `Electrical demand at ${Math.floor(demandPercent)}% of limit. Risk of demand charge penalty.`,
          recommendation: 'Implement peak shaving. Pre-cool building, shed non-critical loads, stage equipment starts.',
          confidence: 98,
          action: {
            type: 'PEAK_SHAVE',
            description: 'Activate demand response',
          },
        });
      }
    }
    
    // Water - Leak detected
    if (asset.type === 'Leak Sensor' && m.flowDetected && m.duration > 15) {
      anomalies.push({
        id: `leak-${asset.id}`,
        type: 'WATER_LEAK',
        assetId: asset.id,
        severity: 'critical',
        metric: `${m.flowRate} GPM × ${m.duration} min`,
        description: `Continuous water flow detected for ${m.duration} minutes. Estimated waste: ${(m.flowRate * m.duration).toFixed(0)} gallons.`,
        recommendation: 'Dispatch maintenance immediately. Isolate zone if possible. Check for running fixture or burst pipe.',
        confidence: m.leakConfidence,
        action: {
          type: 'DISPATCH_PLUMBER',
          description: 'Emergency maintenance call',
        },
      });
    }
    
    // Parking - EV Charger fault
    if (asset.type === 'EV Charger' && m.state === 'FAULT') {
      anomalies.push({
        id: `ev-${asset.id}`,
        type: 'EV_CHARGER_FAULT',
        assetId: asset.id,
        severity: 'warning',
        metric: m.faultCode,
        description: `EV charger fault: ${m.faultCode}. Vehicle connected, owner Badge ${m.vehicleOwner}.`,
        recommendation: 'Take charger offline. Notify vehicle owner. Schedule electrical inspection.',
        confidence: 97,
        action: {
          type: 'NOTIFY_DRIVER',
          description: 'Send notification to badge holder',
        },
      });
    }
  });
  
  return anomalies.sort((a, b) => {
    const sevOrder = { critical: 0, warning: 1, info: 2 };
    return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
  });
};

const getAssetAIStatus = (asset, anomalies) => {
  const assetAnomalies = anomalies.filter(a => a.assetId === asset.id);
  
  if (asset.status === 'standby' || asset.status === 'offline') return AI_STATUS.STANDBY;
  
  const hasCritical = assetAnomalies.some(a => a.severity === 'critical');
  const hasWarning = assetAnomalies.some(a => a.severity === 'warning');
  
  if (hasCritical || asset.status === 'fault' || asset.status === 'alarm') return AI_STATUS.CRITICAL;
  if (hasWarning || asset.status === 'warning' || asset.status === 'alert') return AI_STATUS.WARNING;
  return AI_STATUS.NORMAL;
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

// Building Operations Center Node
const BuildingOpsNode = ({ data }) => (
  <div className="relative group">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-400 !border-2 !border-white" />
    
    <div className="absolute -inset-2 bg-gradient-to-r from-emerald-400/30 via-cyan-400/30 to-emerald-400/30 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
    
    <div className="relative bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-2 border-emerald-400/50 rounded-2xl p-6 min-w-[280px] shadow-2xl shadow-emerald-500/20">
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400 shadow-lg shadow-emerald-400/50" />
      </div>
      
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-emerald-400/30 to-cyan-500/30 rounded-xl flex items-center justify-center border border-emerald-400/50">
          <span className="text-3xl">🏢</span>
        </div>
      </div>
      
      <div className="text-center">
        <div className="text-sm font-semibold text-white tracking-wide">{data.label}</div>
        <div className="text-xs text-slate-300 mt-1">{data.sublabel}</div>
      </div>
      
      <div className="mt-4 pt-3 border-t border-slate-600/50 grid grid-cols-3 gap-2">
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-300">{data.occupancy}%</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Occupied</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-cyan-300">{data.energyToday}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">MWh</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-amber-300">{data.greenScore}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Score</div>
        </div>
      </div>
      
      <div className="mt-3 flex items-center justify-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[10px] text-emerald-300 font-medium">BMS Connected</span>
      </div>
    </div>
  </div>
);

// Building Gateway Node
const BuildingGatewayNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  
  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-emerald-400 !border-2 !border-white" />
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      <div className={`absolute -inset-1.5 bg-gradient-to-r ${isOnline ? 'from-emerald-400/30 to-teal-400/30' : 'from-red-400/30 to-orange-400/30'} rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-300`} />
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${isOnline ? 'border-emerald-400/60' : 'border-red-400/60'} rounded-xl p-4 min-w-[300px] shadow-xl transition-all duration-300 group-hover:scale-105`}>
        <div className="absolute -top-1 -right-1 w-3 h-3">
          {isOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${isOnline ? 'bg-emerald-400' : 'bg-red-400'} shadow-lg`} />
        </div>
        
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOnline ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
            <span className="text-xl">🏗️</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{data.name}</div>
            <div className="text-[10px] text-slate-400 truncate">{data.location}</div>
          </div>
        </div>
        
        {/* Occupancy bar */}
        <div className="mt-3">
          <div className="flex justify-between text-[10px] mb-1">
            <span className="text-slate-400">Building Occupancy</span>
            <span className="text-emerald-300 font-bold">{data.currentOccupants} / {data.maxOccupancy}</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full bg-emerald-500 transition-all"
              style={{ width: `${data.occupancy}%` }}
            />
          </div>
        </div>
        
        <div className="mt-3 pt-2 border-t border-slate-500/30 grid grid-cols-3 gap-2 text-xs">
          <div>
            <span className="text-slate-400">Systems</span>
            <div className="font-semibold text-cyan-300">{data.assetCount}</div>
          </div>
          <div className="text-center">
            <span className="text-slate-400">Alerts</span>
            <div className={`font-semibold ${data.activeAlerts > 0 ? 'text-amber-300' : 'text-slate-300'}`}>{data.activeAlerts}</div>
          </div>
          <div className="text-right">
            <span className="text-slate-400">Energy</span>
            <div className="font-semibold text-emerald-300">{data.energyToday} MWh</div>
          </div>
        </div>
        
        <div className="mt-2 text-center">
          <span className="text-[9px] px-2 py-0.5 bg-slate-800/50 rounded-full text-cyan-300 font-mono">
            {data.stratusModel} • {data.connectivity}
          </span>
        </div>
      </div>
    </div>
  );
};

// Building Asset Node
const BuildingAssetNode = ({ data }) => {
  const categoryStyle = CATEGORY_STYLES[data.category] || CATEGORY_STYLES.hvac;
  const aiStatus = data.aiStatus || AI_STATUS.NORMAL;
  const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.WARNING;
  const m = data.metrics || {};

  const renderMetrics = () => {
    switch (data.category) {
      case 'hvac':
        if (data.type === 'Chiller') {
          return (
            <div className="grid grid-cols-3 gap-1 text-center">
              <div className="rounded px-1 py-0.5 bg-slate-700/50">
                <div className="text-[10px] font-bold text-cyan-300">{m.loadPercent || 0}%</div>
                <div className="text-[8px] text-slate-500">LOAD</div>
              </div>
              <div className="rounded px-1 py-0.5 bg-slate-700/50">
                <div className="text-[10px] font-bold text-slate-300">{m.supplyTemp || '--'}°F</div>
                <div className="text-[8px] text-slate-500">CHWS</div>
              </div>
              <div className={`rounded px-1 py-0.5 ${m.kWPerTon > m.baselineKW * 1.15 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
                <div className={`text-[10px] font-bold ${m.kWPerTon > m.baselineKW * 1.15 ? 'text-amber-300' : 'text-slate-300'}`}>{m.kWPerTon || '--'}</div>
                <div className="text-[8px] text-slate-500">kW/T</div>
              </div>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className={`rounded px-1 py-0.5 ${Math.abs(m.supplyAirTemp - m.setpoint) > 5 ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${Math.abs(m.supplyAirTemp - m.setpoint) > 5 ? 'text-red-300' : 'text-cyan-300'}`}>{m.supplyAirTemp}°F</div>
              <div className="text-[8px] text-slate-500">SAT</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.supplyFanSpeed}%</div>
              <div className="text-[8px] text-slate-500">FAN</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${m.filterDP > m.filterAlarm * 0.9 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.filterDP > m.filterAlarm * 0.9 ? 'text-amber-300' : 'text-slate-300'}`}>{m.filterDP}"</div>
              <div className="text-[8px] text-slate-500">FLTR</div>
            </div>
          </div>
        );
      case 'elevator':
        return (
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-cyan-300">F{m.currentFloor}</div>
              <div className="text-[8px] text-slate-500">{m.direction === 'up' ? '↑' : m.direction === 'down' ? '↓' : '●'}</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${m.doorDwellTime > m.baselineDwell * 1.3 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.doorDwellTime > m.baselineDwell * 1.3 ? 'text-amber-300' : 'text-slate-300'}`}>{m.doorDwellTime}s</div>
              <div className="text-[8px] text-slate-500">DOOR</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${m.brakeWear < 50 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.brakeWear < 50 ? 'text-amber-300' : 'text-slate-300'}`}>{m.brakeWear}%</div>
              <div className="text-[8px] text-slate-500">BRAKE</div>
            </div>
          </div>
        );
      case 'access':
        return (
          <div className="grid grid-cols-2 gap-1 text-center">
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-emerald-300">{m.passagesToday}</div>
              <div className="text-[8px] text-slate-500">PASS</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${m.deniedToday > 5 ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.deniedToday > 5 ? 'text-red-300' : 'text-slate-300'}`}>{m.deniedToday}</div>
              <div className="text-[8px] text-slate-500">DENY</div>
            </div>
          </div>
        );
      case 'fire':
        if (data.type === 'Smoke Detector') {
          return (
            <div className={`rounded px-2 py-1.5 ${m.driftPercent > 15 ? 'bg-amber-500/20 border border-amber-400/30' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold text-center ${m.driftPercent > 15 ? 'text-amber-300' : 'text-emerald-300'}`}>
                {m.driftPercent > 15 ? `⚠️ ${m.driftPercent}% drift` : '✓ Normal'}
              </div>
            </div>
          );
        }
        return (
          <div className="rounded px-2 py-1.5 bg-emerald-500/20 border border-emerald-400/30">
            <div className="text-[10px] font-bold text-emerald-300 text-center">
              ✓ {m.systemStatus} • {m.zonesOnline}/{m.zonesTotal} zones
            </div>
          </div>
        );
      case 'lighting':
        return (
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-yellow-300">{m.currentLevel}%</div>
              <div className="text-[8px] text-slate-500">LEVEL</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${m.occupancyState !== m.hvacMode ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.occupancyState === 'Occupied' ? 'text-emerald-300' : 'text-slate-400'}`}>
                {m.occupancyState === 'Occupied' ? 'OCC' : 'VAC'}
              </div>
              <div className="text-[8px] text-slate-500">STATE</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.fixturesOnline}/{m.fixtures}</div>
              <div className="text-[8px] text-slate-500">FIX</div>
            </div>
          </div>
        );
      case 'energy':
        if (data.type === 'Main Meter') {
          const pct = Math.floor((m.demandKW / m.peakLimit) * 100);
          return (
            <div className="space-y-1">
              <div className="flex justify-between text-[10px]">
                <span className="text-slate-400">Demand</span>
                <span className={`font-bold ${pct > 80 ? 'text-amber-300' : 'text-emerald-300'}`}>{m.demandKW} kW</span>
              </div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${pct > 90 ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-2 gap-1 text-center">
            <div className="rounded px-1 py-0.5 bg-emerald-500/20">
              <div className="text-[10px] font-bold text-emerald-300">{m.currentOutput} kW</div>
              <div className="text-[8px] text-slate-500">OUTPUT</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.efficiency}%</div>
              <div className="text-[8px] text-slate-500">EFF</div>
            </div>
          </div>
        );
      case 'water':
        if (data.type === 'Leak Sensor') {
          return (
            <div className={`rounded px-2 py-1.5 ${m.flowDetected ? 'bg-red-500/30 border border-red-400/50' : 'bg-emerald-500/20 border border-emerald-400/30'}`}>
              <div className={`text-[10px] font-bold text-center ${m.flowDetected ? 'text-red-300' : 'text-emerald-300'}`}>
                {m.flowDetected ? `🚨 LEAK ${m.flowRate} GPM` : '✓ No leak'}
              </div>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-2 gap-1 text-center">
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-cyan-300">{m.flowRate} GPM</div>
              <div className="text-[8px] text-slate-500">FLOW</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{(m.usageToday/1000).toFixed(1)}K</div>
              <div className="text-[8px] text-slate-500">GAL</div>
            </div>
          </div>
        );
      case 'parking':
        if (data.type === 'EV Charger') {
          return (
            <div className={`rounded px-2 py-1.5 ${m.state === 'FAULT' ? 'bg-red-500/30 border border-red-400/50' : 'bg-emerald-500/20 border border-emerald-400/30'}`}>
              <div className={`text-[10px] font-bold text-center ${m.state === 'FAULT' ? 'text-red-300' : 'text-emerald-300'}`}>
                {m.state === 'FAULT' ? '⚠️ FAULT' : '✓ Available'}
              </div>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-2 gap-1 text-center">
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-emerald-300">{m.available}</div>
              <div className="text-[8px] text-slate-500">AVAIL</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-cyan-300">{m.evAvailable}/{m.evChargers}</div>
              <div className="text-[8px] text-slate-500">EV</div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      {hasAlert && (
        <div className={`absolute -inset-2 bg-gradient-to-r ${aiStatus.glow} rounded-xl blur-lg opacity-60 ${aiStatus.pulse ? 'animate-pulse' : ''}`} />
      )}
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${hasAlert ? (aiStatus === AI_STATUS.CRITICAL ? 'border-red-400' : 'border-amber-400') : categoryStyle.border} rounded-lg p-3 min-w-[160px] shadow-lg transition-all duration-300 group-hover:scale-105`}>
        
        {/* AI Status Badge */}
        <div className={`absolute -top-2 -right-2 px-2 py-0.5 ${aiStatus.bg} text-white text-[9px] font-bold rounded-full shadow-lg flex items-center gap-1 ${aiStatus.pulse ? 'animate-pulse' : ''}`}>
          <span>{aiStatus.icon}</span>
          <span>{aiStatus.text}</span>
        </div>
        
        {/* Asset header */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br ${categoryStyle.bg}`}>
            <span className="text-lg">{categoryStyle.icon}</span>
          </div>
          <div>
            <div className="text-xs font-bold text-white">{data.id}</div>
            <div className="text-[9px] text-slate-400">{data.type}</div>
          </div>
        </div>
        
        {/* Location */}
        <div className="text-[9px] text-slate-500 mb-2 truncate">{data.location}</div>
        
        {/* Metrics */}
        {renderMetrics()}
      </div>
    </div>
  );
};

// Node types registry
const nodeTypes = {
  buildingOps: BuildingOpsNode,
  buildingGateway: BuildingGatewayNode,
  buildingAsset: BuildingAssetNode,
};

// ════════════════════════════════════════════════════════════════════════════
// AI ACTIVITY PANEL
// ════════════════════════════════════════════════════════════════════════════

const AIActivityPanel = ({ isOpen, onClose, anomalies, assets, gateway }) => {
  const [activeTab, setActiveTab] = useState('alerts');
  const [executingAction, setExecutingAction] = useState(null);
  
  if (!isOpen) return null;
  
  const handleExecuteAction = async (anomaly) => {
    setExecutingAction(anomaly.id);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setExecutingAction(null);
  };
  
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  
  return (
    <div className="absolute top-0 right-0 h-full w-[400px] bg-slate-900/98 backdrop-blur-xl border-l border-slate-700/50 shadow-2xl z-50 flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <span className="text-lg">🏢</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">AI Sentry Building Ops</h3>
              <div className="text-[10px] text-slate-400">{anomalies.length} alerts • {gateway.location}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex items-center gap-2 mt-3">
          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 rounded text-emerald-300 text-xs">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            BMS Connected
          </div>
          <div className="text-[10px] text-slate-500">BACnet/IP Active</div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-slate-700/50">
        <button
          onClick={() => setActiveTab('alerts')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'alerts'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <span className="flex items-center justify-center gap-1">
            🚨 Alerts
            {criticalCount > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full">{criticalCount}</span>
            )}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('comfort')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'comfort'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          🌡️ Comfort
        </button>
        <button
          onClick={() => setActiveTab('energy')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'energy'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          ⚡ Energy
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {activeTab === 'alerts' && (
          <>
            {anomalies.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <div className="text-3xl mb-2">✓</div>
                <div>All systems normal</div>
              </div>
            ) : (
              anomalies.map(anomaly => {
                const asset = assets.find(a => a.id === anomaly.assetId);
                const categoryStyle = CATEGORY_STYLES[asset?.category] || CATEGORY_STYLES.hvac;
                return (
                  <div
                    key={anomaly.id}
                    className={`bg-slate-800/50 rounded-lg border ${
                      anomaly.severity === 'critical' ? 'border-red-500/50' : 'border-amber-500/30'
                    } p-3`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{categoryStyle.icon}</span>
                        <div>
                          <div className="text-sm font-semibold text-white">{anomaly.assetId}</div>
                          <div className="text-[10px] text-slate-400">{asset?.location}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs font-bold ${anomaly.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                          {anomaly.confidence}%
                        </div>
                        <div className="text-[10px] text-slate-500">confidence</div>
                      </div>
                    </div>
                    
                    <div className="text-xs text-slate-300 mb-2">{anomaly.description}</div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${anomaly.severity === 'critical' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
                        {anomaly.metric}
                      </span>
                      <span className="text-[10px] text-slate-500">{anomaly.type.replace(/_/g, ' ')}</span>
                    </div>
                    
                    <div className="bg-slate-700/30 rounded p-2 mb-3">
                      <div className="text-[10px] text-slate-500 uppercase mb-1">AI Recommendation</div>
                      <div className="text-xs text-cyan-300">{anomaly.recommendation}</div>
                    </div>
                    
                    {anomaly.action && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleExecuteAction(anomaly)}
                          disabled={executingAction === anomaly.id}
                          className="flex-1 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:bg-slate-600 text-white text-xs font-medium rounded transition-colors flex items-center justify-center gap-1"
                        >
                          {executingAction === anomaly.id ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>✓ {anomaly.action.type.replace(/_/g, ' ')}</>
                          )}
                        </button>
                        <button className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors">
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}
        
        {activeTab === 'comfort' && (
          <div className="space-y-3">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Zone Comfort Summary</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
                  <div className="text-2xl font-bold text-emerald-300">94%</div>
                  <div className="text-xs text-slate-400">Zones Comfortable</div>
                </div>
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-cyan-300">72°F</div>
                  <div className="text-xs text-slate-400">Avg Temperature</div>
                </div>
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-amber-300">3</div>
                  <div className="text-xs text-slate-400">Hot Calls Today</div>
                </div>
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-purple-300">45%</div>
                  <div className="text-xs text-slate-400">Avg Humidity</div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Active Comfort Issues</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-amber-500/10 rounded border border-amber-500/30">
                  <div>
                    <div className="text-sm text-white">Floors 28-35</div>
                    <div className="text-[10px] text-slate-400">AHU-03 supply temp high</div>
                  </div>
                  <span className="text-amber-300 text-xs">67°F SAT</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-700/50 rounded">
                  <div>
                    <div className="text-sm text-white">Floor 22 Zone B3</div>
                    <div className="text-[10px] text-slate-400">HVAC/Lighting sync issue</div>
                  </div>
                  <span className="text-amber-300 text-xs">Mismatch</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'energy' && (
          <div className="space-y-3">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Today's Energy</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-cyan-300">{gateway.energyToday}</div>
                  <div className="text-xs text-slate-400">MWh Used</div>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
                  <div className="text-2xl font-bold text-emerald-300">{gateway.energyTarget}</div>
                  <div className="text-xs text-slate-400">MWh Target</div>
                </div>
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-[10px] mb-1">
                  <span className="text-slate-400">Budget Usage</span>
                  <span className="text-emerald-300">{Math.floor((gateway.energyToday/gateway.energyTarget)*100)}%</span>
                </div>
                <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(gateway.energyToday/gateway.energyTarget)*100}%` }} />
                </div>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Solar Generation</div>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full border-4 border-emerald-500/30 flex items-center justify-center">
                  <span className="text-2xl">☀️</span>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-300">145 kW</div>
                  <div className="text-xs text-slate-400">Current output • 52% capacity</div>
                  <div className="text-xs text-emerald-300 mt-1">0.82 MWh today</div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Demand Status</div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white">Peak Warning</span>
                  <span className="text-amber-300 text-xs font-bold">74% of limit</span>
                </div>
                <div className="text-xs text-slate-300">1,842 kW current demand. Consider pre-cooling before afternoon peak.</div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="border-t border-slate-700/50 px-4 py-2 bg-slate-800/50">
        <div className="flex items-center justify-between text-[10px] text-slate-500">
          <span>Last BMS poll: 5s ago</span>
          <button className="text-cyan-400 hover:text-cyan-300">Refresh Now</button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE ALERT & STATS
// ════════════════════════════════════════════════════════════════════════════

const ProactiveAlert = ({ anomalies, assets, onViewDetails }) => {
  const criticalAnomalies = anomalies.filter(a => a.severity === 'critical');
  if (criticalAnomalies.length === 0) return null;
  
  const topAnomaly = criticalAnomalies[0];
  const asset = assets.find(a => a.id === topAnomaly.assetId);
  const categoryStyle = CATEGORY_STYLES[asset?.category] || CATEGORY_STYLES.hvac;
  
  return (
    <div className="absolute top-32 left-6 z-40 animate-slide-in-left">
      <div className="bg-gradient-to-r from-red-900/90 to-slate-900/90 backdrop-blur-xl border border-red-500/50 rounded-xl p-4 shadow-2xl shadow-red-500/20 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-500/30 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xl animate-pulse">{categoryStyle.icon}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white mb-1">Building Alert</div>
            <div className="text-xs text-red-200 mb-2">{topAnomaly.description}</div>
            <div className="text-[10px] text-slate-400 mb-3">
              {topAnomaly.assetId} • {asset?.location} • {criticalAnomalies.length > 1 ? `+${criticalAnomalies.length - 1} more` : ''}
            </div>
            <button 
              onClick={onViewDetails}
              className="px-4 py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-medium rounded-lg transition-colors"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatsBar = ({ gateway, anomalies, onOpenPanel }) => {
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-emerald-300 font-bold text-lg">{gateway.occupancy}%</span>
        <span className="text-slate-300 text-xs">Occupied</span>
      </div>
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-cyan-300 font-bold text-lg">{gateway.energyToday}</span>
        <span className="text-slate-300 text-xs">MWh</span>
      </div>
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-amber-300 font-bold text-lg">{gateway.greenScore}</span>
        <span className="text-slate-300 text-xs">Score</span>
      </div>
      {criticalCount > 0 && (
        <div className="flex items-center gap-2 bg-red-500/20 rounded-lg px-3 py-2 border border-red-400/50 animate-pulse">
          <span className="text-red-300 font-bold text-lg">{criticalCount}</span>
          <span className="text-red-300/70 text-xs">Alerts</span>
        </div>
      )}
      <button
        onClick={onOpenPanel}
        className="flex items-center gap-2 bg-emerald-500/20 hover:bg-emerald-500/30 rounded-lg px-4 py-2 border border-emerald-400/50 transition-colors"
      >
        <span className="text-emerald-300 text-sm">🤖</span>
        <span className="text-emerald-300 text-xs font-medium">AI Sentry</span>
        {anomalies.length > 0 && (
          <span className="px-1.5 py-0.5 bg-emerald-500 text-white text-[10px] rounded-full">{anomalies.length}</span>
        )}
      </button>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEGEND
// ════════════════════════════════════════════════════════════════════════════

const Legend = () => (
  <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-600/50 rounded-xl p-4 space-y-3">
    <div className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Systems</div>
    <div className="grid grid-cols-2 gap-2">
      {Object.entries(CATEGORY_STYLES).map(([key, style]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-sm">{style.icon}</span>
          <span className="text-[10px] text-slate-300">{style.label}</span>
        </div>
      ))}
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const BuildingSentry = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [assets] = useState(MOCK_ASSETS);
  const [gateway] = useState(MOCK_BUILDING_GATEWAY);
  const [anomalies, setAnomalies] = useState([]);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const buildTopology = useCallback(() => {
    const detectedAnomalies = detectBuildingAnomalies(assets, gateway);
    setAnomalies(detectedAnomalies);
    
    const newNodes = [];
    const newEdges = [];
    
    const CLOUD_Y = 0;
    const GATEWAY_Y = 200;
    const ASSET_Y_START = 440;
    const CENTER_X = 700;
    const ASSET_GAP = 180;
    const CATEGORY_GAP = 160;
    
    const categories = ['hvac', 'elevator', 'access', 'fire', 'lighting', 'energy', 'water', 'parking'];
    const assetsByCategory = {};
    categories.forEach(cat => {
      assetsByCategory[cat] = assets.filter(a => a.category === cat);
    });
    
    newNodes.push({
      id: 'building-ops',
      type: 'buildingOps',
      position: { x: CENTER_X, y: CLOUD_Y },
      data: {
        label: 'Building Operations',
        sublabel: 'Smart Building Platform',
        occupancy: gateway.occupancy,
        energyToday: gateway.energyToday,
        greenScore: gateway.greenScore,
      },
    });
    
    newNodes.push({
      id: `gateway-${gateway.id}`,
      type: 'buildingGateway',
      position: { x: CENTER_X, y: GATEWAY_Y },
      data: { ...gateway, activeAlerts: detectedAnomalies.length },
    });
    
    newEdges.push({
      id: 'cloud-to-gateway',
      source: 'building-ops',
      target: `gateway-${gateway.id}`,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#34d399', strokeWidth: 2 },
    });
    
    let currentY = ASSET_Y_START;
    
    categories.forEach(category => {
      const categoryAssets = assetsByCategory[category];
      if (categoryAssets.length === 0) return;
      
      const startX = CENTER_X - ((categoryAssets.length - 1) * ASSET_GAP) / 2;
      
      categoryAssets.forEach((asset, idx) => {
        const aiStatus = getAssetAIStatus(asset, detectedAnomalies);
        const assetId = `asset-${asset.id}`;
        
        newNodes.push({
          id: assetId,
          type: 'buildingAsset',
          position: { x: startX + idx * ASSET_GAP, y: currentY },
          data: { ...asset, aiStatus },
        });
        
        const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.WARNING;
        const edgeColor = hasAlert 
          ? (aiStatus === AI_STATUS.CRITICAL ? '#ef4444' : '#f59e0b')
          : '#34d399';
        
        newEdges.push({
          id: `gateway-to-${assetId}`,
          source: `gateway-${gateway.id}`,
          target: assetId,
          type: 'smoothstep',
          animated: true,
          style: { stroke: edgeColor, strokeWidth: 2 },
        });
      });
      
      currentY += CATEGORY_GAP;
    });
    
    return { nodes: newNodes, edges: newEdges };
  }, [assets, gateway]);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const { nodes: newNodes, edges: newEdges } = buildTopology();
      setNodes(newNodes);
      setEdges(newEdges);
      setLoading(false);
    }, 1000);
  }, [buildTopology, setNodes, setEdges]);

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-900/95 to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-emerald-500/30 to-cyan-600/30 rounded-xl flex items-center justify-center border border-emerald-400/30">
                <span className="text-xl">🏢</span>
              </span>
              Building Sentry
            </h1>
            <p className="text-slate-300 text-sm mt-1">AI-Powered Smart Building Operations • Energy • Comfort • Safety</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded border border-emerald-500/30">
                {gateway.location}
              </span>
              <span className="text-[10px] px-2 py-1 bg-slate-700/50 text-slate-400 rounded">
                MWC Barcelona 2025
              </span>
            </div>
          </div>
          <StatsBar gateway={gateway} anomalies={anomalies} onOpenPanel={() => setIsAIPanelOpen(true)} />
        </div>
      </div>
      
      <ProactiveAlert anomalies={anomalies} assets={assets} onViewDetails={() => setIsAIPanelOpen(true)} />
      
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-emerald-300 text-lg font-medium">AI Sentry Initializing...</div>
            <div className="text-slate-400 text-sm mt-1">Connecting to Building Management System</div>
          </div>
        </div>
      )}
      
      {!loading && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.1 }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.4 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1} color="rgba(52, 211, 153, 0.15)" />
          <Controls className="!bg-slate-700/90 !border-slate-600/50 !rounded-xl" />
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'buildingOps') return '#34d399';
              if (node.type === 'buildingGateway') return '#22d3ee';
              if (node.data?.aiStatus === AI_STATUS.CRITICAL) return '#ef4444';
              if (node.data?.aiStatus === AI_STATUS.WARNING) return '#f59e0b';
              return '#60a5fa';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
            className="!bg-slate-800/90 !border-slate-600/50 rounded-xl"
          />
          <Panel position="bottom-left"><Legend /></Panel>
        </ReactFlow>
      )}
      
      <AIActivityPanel 
        isOpen={isAIPanelOpen} 
        onClose={() => setIsAIPanelOpen(false)} 
        anomalies={anomalies}
        assets={assets}
        gateway={gateway}
      />
      
      <style>{`
        @keyframes slide-in-left { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-left { animation: slide-in-left 0.3s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default BuildingSentry;
